import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Users,
  UserCheck,
  ShieldCheck,
  AlertTriangle,
  Plus,
} from 'lucide-react';

interface Driver {
  id: string;
  name: string;
  email: string;
  phone: string;
  license_class: string;
  license_expiry: string;
  assigned_vehicle: string | null;
  status: 'active' | 'inactive' | 'suspended';
}

interface DriversResponse {
  drivers: Driver[];
  metrics: {
    total_drivers: number;
    active: number;
    cdl_holders: number;
    expiring_licenses: number;
  };
}

const statusColors: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  inactive: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function DriversPage() {
  const { data, isLoading } = useApi<DriversResponse>('/api/drivers');
  const [showNew, setShowNew] = useState(false);

  const metrics = data?.metrics ?? { total_drivers: 0, active: 0, cdl_holders: 0, expiring_licenses: 0 };
  const drivers = data?.drivers ?? [];

  const metricCards = [
    { label: 'Total Drivers', value: metrics.total_drivers, icon: Users, color: 'text-emerald-400' },
    { label: 'Active', value: metrics.active, icon: UserCheck, color: 'text-emerald-400' },
    { label: 'CDL Holders', value: metrics.cdl_holders, icon: ShieldCheck, color: 'text-blue-400' },
    { label: 'Expiring Licenses', value: metrics.expiring_licenses, icon: AlertTriangle, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-zinc-100">Drivers</h1>
        </div>
        <button
          onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Driver
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">{m.label}</span>
              <m.icon className={`h-5 w-5 ${m.color}`} />
            </div>
            <p className="mt-2 text-2xl font-semibold text-zinc-100">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-400">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">License Class</th>
                <th className="px-4 py-3 font-medium">License Expiry</th>
                <th className="px-4 py-3 font-medium">Assigned Vehicle</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Loading drivers...</td>
                </tr>
              ) : drivers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">No drivers found</td>
                </tr>
              ) : (
                drivers.map((driver) => (
                  <tr key={driver.id} className="hover:bg-zinc-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-zinc-100">{driver.name}</td>
                    <td className="px-4 py-3 text-zinc-300">{driver.email}</td>
                    <td className="px-4 py-3 text-zinc-300">{driver.phone}</td>
                    <td className="px-4 py-3 text-zinc-300">{driver.license_class}</td>
                    <td className="px-4 py-3 text-zinc-400">{new Date(driver.license_expiry).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-zinc-300">{driver.assigned_vehicle ?? '--'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${statusColors[driver.status] ?? ''}`}>
                        {driver.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
